'use client';

import type { ReactNode } from 'react';
import { useState, useMemo, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';

import { createSafeContext } from '@/utils/react/createSafeContext';
import { USER_QUERIES } from '@/infrastructure/user/utils/queries';
import type { Cookie } from '@/models/browser/cookie';

type UserLoggedInKeyContextValue = {
  userLoggedInKey: Cookie;
  isLoggedIn: boolean;
  setUserLoggedInKey: (value: Cookie) => void;
};

const [UserLoggedInKeyContextProvider, useUserLoggedInKeyContext] =
  createSafeContext<UserLoggedInKeyContextValue>('UserLoggedInKey');

type UserLoggedInKeyProviderProps = {
  initialUserLoggedInKey: Cookie;
  children: ReactNode;
};

export const UserLoggedInKeyProvider = ({ initialUserLoggedInKey, children }: UserLoggedInKeyProviderProps) => {
  const [userLoggedInKeyState, setUserLoggedInKeyState] = useState<Cookie>(initialUserLoggedInKey);

  const { data: user } = useQuery(USER_QUERIES.getCookieUser());

  const isLoggedIn = useMemo(() => {
    return Boolean(userLoggedInKeyState && user?.id);
  }, [userLoggedInKeyState, user?.id]);

  const setUserLoggedInKey = useCallback(({ name, value }: Cookie) => {
    setUserLoggedInKeyState((prev) => {
      return {
        ...prev,
        name,
        value,
      };
    });
  }, []);

  const contextValue = useMemo(
    () => ({
      userLoggedInKey: userLoggedInKeyState,
      isLoggedIn,
      setUserLoggedInKey,
    }),
    [userLoggedInKeyState, isLoggedIn, setUserLoggedInKey],
  );

  return <UserLoggedInKeyContextProvider value={contextValue}>{children}</UserLoggedInKeyContextProvider>;
};

export const useUserLoggedInKey = useUserLoggedInKeyContext;
